import './index.css';
import { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import type {
  CollectionBindingOverridesMap,
  ComponentContentSchema,
  ContentOverridesMap,
  DesignOverridesMap,
  PageDataMap,
  ResolvedRegistry,
  SiteManifest,
  SlotGroupOverridesMap,
  StyleOverridesMap,
} from '@vibesites/runtime';
import { expandTemplatePages, mergePages, mergeSections, resolvePageTokens, resolveSectionOverrides } from '@vibesites/runtime';
import {
  VibesitesProvider,
  DefaultLayout,
  PageRenderer
} from '@vibesites/runtime/react';
import { blockCatalog } from '@vibesites/blocks';
import { registry as themeRegistry } from './registry';
import { tokens as baseTokens } from '@vibesites/theme-corporate-multidomain';
import {
  designOverrides,
  contentOverrides,
  slotGroupOverrides,
  collectionBindingOverrides,
  sectionOverrides,
  styleOverrides,
} from './vibesites-overrides';
import { company, propertySummaries } from './vibesites-company-data';
import { fetchPropertyDetail } from './components/propertyUtils';
import pagesJson from './pages.json';

const manifest: SiteManifest = {
  property_id: '',
  site_id: 'case-associates-corprate-corporate-multidomain',
  theme: 'theme-corporate-multidomain',
};

const registry = {
  ...themeRegistry,
  ...Object.fromEntries(
    Object.entries(blockCatalog).map(([id, block]) => [`block:${id}`, block.component])
  ),
} as ResolvedRegistry;

const typedDesignOverrides = designOverrides as DesignOverridesMap;
const typedContentOverrides = contentOverrides as ContentOverridesMap;
const typedSlotGroupOverrides = slotGroupOverrides as unknown as SlotGroupOverridesMap;
const typedCollectionBindingOverrides = collectionBindingOverrides as unknown as CollectionBindingOverridesMap;
const typedStyleOverrides = styleOverrides as StyleOverridesMap;

const contentSchemas: Record<string, ComponentContentSchema> = {};
for (const [slot, comp] of Object.entries(registry)) {
  const schema = comp && typeof comp === 'function'
    ? (comp as { contentSchema?: ComponentContentSchema }).contentSchema
    : undefined;
  if (schema) contentSchemas[slot] = schema;
}

// Derive propertiesByState index from slim summaries
const STATE_NAMES: Record<string, string> = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',
  CO:'Colorado',CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',
  KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',
  MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',
  MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',
  OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',
  VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
  DC:'District of Columbia',
};

function buildPropertiesByState(summaries: any[]) {
  const index: Record<string, any> = {};
  for (const prop of summaries) {
    const { _stateSlug, _citySlug } = prop;
    if (!_stateSlug || !_citySlug) continue;
    const abbr = (prop.property_address_state || '').toUpperCase();
    if (!index[_stateSlug]) {
      index[_stateSlug] = { slug: _stateSlug, abbr, name: STATE_NAMES[abbr] || abbr, cities: {} };
    }
    if (!index[_stateSlug].cities[_citySlug]) {
      index[_stateSlug].cities[_citySlug] = { slug: _citySlug, name: prop.property_address_city || _citySlug, properties: [] };
    }
    index[_stateSlug].cities[_citySlug].properties.push(prop);
  }
  return index;
}

const propertiesByState = buildPropertiesByState(propertySummaries);

// Lazy property detail loading
const propertyDetailCache = new Map<string, any>();

function usePropertyDetail(slug: string | undefined) {
  const summary = slug ? propertySummaries.find((p: any) => p._slug === slug) : null;
  const [full, setFull] = useState<any>(null);

  useEffect(() => {
    if (!slug) { setFull(null); return; }
    const cached = propertyDetailCache.get(slug);
    if (cached) { setFull(cached); return; }
    setFull(null);
    fetchPropertyDetail(slug, summary?.multihub_property_id)
      .then(data => {
        if (!data) return;
        const merged = { ...data, _slug: summary?._slug, _stateSlug: summary?._stateSlug, _citySlug: summary?._citySlug };
        propertyDetailCache.set(slug, merged);
        setFull(merged);
      })
      .catch(() => {});
  }, [slug]);

  return { summary, property: full || summary, isLoading: !!slug && !full };
}

// Page expansion and routing
const resolvedPages = mergePages(pagesJson as PageDataMap);
const expandedPages = expandTemplatePages(resolvedPages, propertiesByState, propertySummaries);
const pages = Object.fromEntries(expandedPages.map(p => [p.alias, resolvePageTokens(p)]));

const pathToAlias: Record<string, string> = {};
for (const p of expandedPages) {
  if (p.visible !== false) {
    pathToAlias[p.path.replace(/\/+$/, '') || '/'] = p.alias;
  }
}

const propertySlugByAlias: Record<string, string> = {};
for (const state of Object.values(propertiesByState) as any[]) {
  for (const city of Object.values(state.cities) as any[]) {
    for (const prop of city.properties) {
      const suffix = `${state.slug}--${city.slug}--${prop._slug}`;
      for (const ep of expandedPages) {
        if (ep.alias.endsWith(suffix)) {
          propertySlugByAlias[ep.alias] = prop._slug;
        }
      }
    }
  }
}

function resolvePageFromPath(pathname: string): { alias: string; page: typeof pages[string]; propertySlug: string | undefined } {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const alias = pathToAlias[normalized];
  if (alias && pages[alias]) {
    return { alias, page: pages[alias], propertySlug: propertySlugByAlias[alias] };
  }
  return { alias: 'home', page: pages.home, propertySlug: undefined };
}

// SPA Router (disabled) — SSG generates standalone HTML for every page, so
// full page loads are used instead of client-side navigation. This prevents
// third-party scripts (e.g. ResidentBoost chatbot) from persisting across
// navigations since the DOM resets on each load.
//
// To re-enable SPA navigation:
//   1. Uncomment useCurrentPath, navigate, __vibesites_navigate, and the
//      document click listener below.
//   2. In App(), replace  const currentPath = window.location.pathname;
//      with               const currentPath = useCurrentPath();
//
// function useCurrentPath(): string {
//   const [p, setP] = useState(window.location.pathname);
//   useEffect(() => {
//     const onPopState = () => setP(window.location.pathname);
//     window.addEventListener('popstate', onPopState);
//     return () => window.removeEventListener('popstate', onPopState);
//   }, []);
//   return p;
// }
//
// function navigate(to: string) {
//   window.history.pushState(null, '', to);
//   window.dispatchEvent(new PopStateEvent('popstate'));
//   window.scrollTo(0, 0);
// }
//
// (window as any).__vibesites_navigate = navigate;
//
// document.addEventListener('click', (e) => {
//   const anchor = (e.target as HTMLElement).closest('a');
//   if (!anchor) return;
//   const href = anchor.getAttribute('href');
//   if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
//   e.preventDefault();
//   navigate(href);
// });

function Loading() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #e0e0e0', borderTopColor: '#333', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
        <p style={{ color: '#666' }}>Loading...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  const currentPath = window.location.pathname;
  const { alias, page: currentPage, propertySlug } = resolvePageFromPath(currentPath);
  const { property: pageProperty } = usePropertyDetail(propertySlug);
  const activeProperty = pageProperty || propertySummaries[0] || {};

  const mergedSections = useMemo(() => {
    const pageCtx = {
      alias,
      path: currentPath,
      template: currentPage?.template,
      baseAlias: currentPage?.template ? alias.split('--')[0] : undefined,
    };
    const combined = resolveSectionOverrides(sectionOverrides, pageCtx);
    if (combined.length === 0) return currentPage?.sections || [];
    return mergeSections(currentPage?.sections || [], combined);
  }, [alias, currentPage, currentPath]);

  if (!currentPage) return <Loading />;

  return (
    <VibesitesProvider
      manifest={manifest}
      property={activeProperty}
      registry={registry}
      page={{
        alias,
        path: currentPath,
        template: currentPage.template,
        baseAlias: currentPage.template ? alias.split('--')[0] : undefined,
        title: typeof currentPage.title === 'function' ? currentPage.title(activeProperty) : (currentPage.title || ''),
        description: typeof currentPage.description === 'function' ? currentPage.description(activeProperty) : (currentPage.description || ''),
      }}
      baseTokens={baseTokens}
      designOverrides={typedDesignOverrides}
      contentOverrides={typedContentOverrides}
      slotGroupOverrides={typedSlotGroupOverrides}
      collectionBindingOverrides={typedCollectionBindingOverrides}
      styleOverrides={typedStyleOverrides}
      companyData={{ company, properties: propertySummaries }}
      contentSchemas={contentSchemas}
    >
      <DefaultLayout>
        <PageRenderer key={alias} sections={mergedSections} />
      </DefaultLayout>
    </VibesitesProvider>
  );
}

const container = document.getElementById('root');
if (container) createRoot(container).render(<App />);
